import * as THREE from 'three';
import AssetManager from './managers/AssetManager';
import socketService from './SocketService';

type TrailUpdater = (projectileMesh: THREE.Object3D, deltaTime: number) => void;

export interface VisualEffectHelperLike {
  createTrail(projectileMesh: THREE.Object3D): {
    particleTrail: THREE.Object3D;
    updateTrail: (projectile: THREE.Object3D, deltaTime: number) => void;
  };
  createExplosion(position: THREE.Vector3, radius?: number): void;
}

type Projectile = {
  mesh: THREE.Object3D;
  velocity: THREE.Vector3;
  lastPos: THREE.Vector3;
  age: number;
  ignoreAge?: number;
  ignoreObject?: THREE.Object3D;
  trailMesh?: THREE.Object3D;
  updateTrail?: TrailUpdater;
  weapon?: string;
};
const DEFAULT_PROJECTILE_SPEED = 100;

class ProjectileSystem {
  visualEffectHelper: VisualEffectHelperLike;
  scene: THREE.Scene;
  collidableObjects: THREE.Object3D[];
  projectilePool: THREE.Object3D[];
  projectiles: Projectile[];
  _tmpV1: THREE.Vector3;
  _tmpV2: THREE.Vector3;
  _tmpV3: THREE.Vector3;

  constructor(
    scene: THREE.Scene,
    collidableObjects: THREE.Object3D[],
    visualEffectHelper: VisualEffectHelperLike
  ) {
    this.visualEffectHelper = visualEffectHelper;
    this.scene = scene;
    this.collidableObjects = collidableObjects;

    // Simple pool for projectile meshes
    this.projectilePool = [];
    this.projectiles = [];

    // Temp vectors to avoid GC
    this._tmpV1 = new THREE.Vector3();
    this._tmpV2 = new THREE.Vector3();
    this._tmpV3 = new THREE.Vector3();
    this._activeWeapon = 'rocket';
  }

  // Function to update projectiles and handle interactions
  update(deltaTime: number) {
    if (this.projectiles.length === 0) return;

    for (let index = this.projectiles.length - 1; index >= 0; index--) {
      const projectile = this.projectiles[index];
      // Move the projectile
      projectile.age += deltaTime;
      projectile.lastPos.copy(projectile.mesh.position);
      projectile.mesh.position.addScaledVector(projectile.velocity, deltaTime);

      // Check for collisions
      const collision = this.checkCollision(projectile);
      if (collision) {
        const { object: collisionTarget, hitPoint } = collision;
        console.log('Collision detected', collisionTarget);
        // Create explosion effect through VisualEffectHelper
        const weapon = projectile.weapon || this._activeWeapon;
        const radius = this._explosionRadiusForWeapon(weapon);
        this.visualEffectHelper.createExplosion(projectile.mesh.position.clone(), radius);
        AssetManager.playSound('tankFireSound');

        const objectInfo = collisionTarget.userData.info;
        if (objectInfo && objectInfo.isEnemy) {
          const targetId = objectInfo.userId ?? objectInfo.playerId ?? objectInfo.id;
          if (targetId) socketService.dealDamage(25, targetId);
        }

        // Shield-specific reaction: add ripple hit point if available
        const anyTarget: any = collisionTarget as any;
        if (anyTarget && anyTarget.userData && typeof anyTarget.userData.addShieldHit === 'function') {
          try { anyTarget.userData.addShieldHit(hitPoint.clone()); } catch {}
        }

        this.removeProjectile(index);
        continue;
      }

      // Remove if out of bounds
      if (this.isOutOfBounds(projectile.mesh.position)) {
        this.removeProjectile(index);
        continue;
      }

      // Update the trail
      if (projectile.updateTrail) projectile.updateTrail(projectile.mesh, deltaTime);
    }
  }

  // Check if projectile is out of the defined boundaries
  isOutOfBounds(position: THREE.Vector3) {
    return Math.abs(position.x) > 200 || Math.abs(position.z) > 200;
  }

  // Check for collisions with scene objects using swept sphere test
  // Returns the collided object and an approximate hit point on its surface (for shields)
  checkCollision(projectile: Projectile): { object: THREE.Object3D; hitPoint: THREE.Vector3 } | null {
    const start = projectile.lastPos;
    const end = projectile.mesh.position;
    const radius = 0.3; // projectile approximate radius
    for (let i = 0; i < this.collidableObjects.length; i++) {
      const obj = this.collidableObjects[i];
      // Ignore if object is the projectile itself
      if (obj === projectile.mesh) continue;
      // Optionally ignore the owner object for a brief time to avoid self-collisions
      if (
        projectile.ignoreObject &&
        obj === projectile.ignoreObject &&
        (projectile.ignoreAge ?? 0) > projectile.age
      ) {
        continue;
      }
      const collider = this._getCollider(obj);
      if (!collider) continue;
      if (collider.type === 'sphere') {
        const center = this._getWorldSphereCenter(obj, collider);
        const hit = this._segmentSphereHitPoint(start, end, center, collider.radius);
        if (hit) {
          return { object: obj, hitPoint: hit };
        }
      } else if (collider.type === 'aabb') {
        // Quick sphere vs AABB by inflating AABB with projectile radius and testing segment endpoints
        const worldBox = this._getWorldAABB(obj, collider);
        if (
          worldBox.expandByScalar(radius).intersectsBox(this._segmentAABB(start, end))
        ) {
          const boxCenter = worldBox.getCenter(new THREE.Vector3());
          const hitPoint = this._closestPointOnSegment(start, end, boxCenter);
          return { object: obj, hitPoint };
        }
      }
    }
    return null;
  }

  _segmentIntersectsSphere(
    p0: THREE.Vector3,
    p1: THREE.Vector3,
    center: THREE.Vector3,
    radius: number
  ) {
    // Compute the squared distance from center to segment
    const v = this._tmpV1.copy(p1).sub(p0); // segment vector
    const w = this._tmpV2.copy(center).sub(p0);
    const c1 = w.dot(v);
    let distSq;
    if (c1 <= 0) {
      distSq = center.distanceToSquared(p0);
    } else {
      const c2 = v.dot(v);
      if (c2 <= c1) {
        distSq = center.distanceToSquared(p1);
      } else {
        const b = c1 / c2;
        const pb = this._tmpV3.copy(v).multiplyScalar(b).add(p0);
        distSq = center.distanceToSquared(pb);
      }
    }
    return distSq <= radius * radius;
  }

  _segmentSphereHitPoint(
    p0: THREE.Vector3,
    p1: THREE.Vector3,
    center: THREE.Vector3,
    radius: number
  ): THREE.Vector3 | null {
    if (!this._segmentIntersectsSphere(p0, p1, center, radius)) return null;
    const v = this._tmpV1.copy(p1).sub(p0);
    const w = this._tmpV2.copy(center).sub(p0);
    const c1 = w.dot(v);
    const c2 = v.dot(v);
    let pb = new THREE.Vector3();
    if (c1 <= 0) pb.copy(p0);
    else if (c2 <= c1) pb.copy(p1);
    else pb.copy(v.multiplyScalar(c1 / c2).add(p0));
    const dir = pb.clone().sub(center);
    if (dir.lengthSq() < 1e-6) return center.clone();
    dir.normalize().multiplyScalar(radius);
    return center.clone().add(dir);
  }

  _closestPointOnSegment(p0: THREE.Vector3, p1: THREE.Vector3, point: THREE.Vector3) {
    const v = this._tmpV1.copy(p1).sub(p0);
    const w = this._tmpV2.copy(point).sub(p0);
    const c1 = w.dot(v);
    if (c1 <= 0) return p0.clone();
    const c2 = v.dot(v);
    if (c2 <= c1) return p1.clone();
    const b = c1 / c2;
    return this._tmpV3.copy(v).multiplyScalar(b).add(p0).clone();
  }

  _getCollider(obj: any) {
    if (!obj.userData._collider) {
      // Tanks -> sphere; everything else -> AABB
      const info = obj.userData?.info;
      if (
        info &&
        (info.objectName === 'enemyTankModel' || info.name === 'MyTankModel')
      ) {
        const box = new THREE.Box3().setFromObject(obj);
        const size = new THREE.Vector3();
        box.getSize(size);
        const center = new THREE.Vector3();
        box.getCenter(center);
        const objCenter = new THREE.Vector3();
        obj.getWorldPosition(objCenter);
        const offset = center.sub(objCenter); // world offset from pivot
        const radius = size.length() * 0.35; // shrink factor to better match tank body
        obj.userData._collider = { type: 'sphere', radius, offset };
      } else {
        // Store local AABB once
        let localBox;
        if (obj.geometry && obj.geometry.boundingBox) {
          localBox = obj.geometry.boundingBox.clone();
        } else {
          // Fallback: compute AABB on demand (world space)
          obj.userData._collider = { type: 'aabb' };
          return obj.userData._collider;
        }
        obj.userData._collider = { type: 'aabb', localBox };
      }
    }
    return obj.userData._collider;
  }

  _getWorldSphereCenter(obj: any, collider: any) {
    const c = new THREE.Vector3();
    obj.getWorldPosition(c);
    return c.add(collider.offset);
  }

  _getWorldAABB(obj: any, collider: any) {
    if (collider.localBox) {
      const worldBox = collider.worldBox || new THREE.Box3();
      if (!collider.worldBox) collider.worldBox = worldBox;
      worldBox.copy(collider.localBox).applyMatrix4(obj.matrixWorld);
      return worldBox;
    }
    // Fallback: compute from object once (should be rare)
    const box = new THREE.Box3().setFromObject(obj);
    return box;
  }

  _segmentAABB(p0: THREE.Vector3, p1: THREE.Vector3) {
    // Build an AABB that bounds the segment (cheap broadphase)
    const min = new THREE.Vector3(
      Math.min(p0.x, p1.x),
      Math.min(p0.y, p1.y),
      Math.min(p0.z, p1.z)
    );
    const max = new THREE.Vector3(
      Math.max(p0.x, p1.x),
      Math.max(p0.y, p1.y),
      Math.max(p0.z, p1.z)
    );
    return new THREE.Box3(min, max);
  }

  // Remove the projectile and its trail from the scene
  removeProjectile(index: number) {
    const projectile = this.projectiles[index];
    this.scene.remove(projectile.mesh);
    if (projectile.trailMesh) {
      this.scene.remove(projectile.trailMesh);
      const anyTrail = projectile.trailMesh as any;
      anyTrail.geometry && anyTrail.geometry.dispose && anyTrail.geometry.dispose();
      anyTrail.material && anyTrail.material.dispose && anyTrail.material.dispose();
    }
    // Return projectile mesh to pool for reuse
    if (projectile.mesh) {
      projectile.mesh.visible = false;
      projectile.mesh.position.set(0, -9999, 0);
      this.projectilePool.push(projectile.mesh);
    }
    this.projectiles.splice(index, 1);
  }

  // Create a new projectile and add it to the scene
  fireProjectile(
    initialPosition: THREE.Vector3,
    direction: THREE.Quaternion,
    ignoreFor?: THREE.Object3D,
    weaponOverride?: string
  ) {
    // Position the projectile
    const weapon = weaponOverride ?? this._activeWeapon;
    const projectileMesh = this._createProjectileForWeapon(weapon);
    projectileMesh.position.copy(initialPosition);

    // Pre-rotation for aligning the projectile direction
    const preRotationQuaternion = new THREE.Quaternion();
    preRotationQuaternion.setFromAxisAngle(
      new THREE.Vector3(1, 0, 0),
      Math.PI / 2
    );

    // Combine the pre-rotation with the direction quaternion
    const finalQuaternion = direction.clone().multiply(preRotationQuaternion);
    projectileMesh.quaternion.copy(finalQuaternion);

    const speed = this._getSpeedForWeapon(weapon);
    const velocity = new THREE.Vector3(0, 0, 1).multiplyScalar(speed);
    velocity.applyQuaternion(direction);

    const projectile: Projectile = {
      mesh: projectileMesh,
      velocity: velocity,
      lastPos: projectileMesh.position.clone(),
      age: 0,
      ignoreAge: ignoreFor ? 0.2 : 0,
      ignoreObject: ignoreFor,
      weapon,
    };

    // Create and update particle trail through VisualEffectHelper
    const { particleTrail, updateTrail } = this.visualEffectHelper.createTrail(
      projectileMesh
    );
    projectile.trailMesh = particleTrail;
    projectile.updateTrail = updateTrail as TrailUpdater;

    this.projectiles.push(projectile);
    this.scene.add(projectileMesh);

    AssetManager.playSound('explosionSound');
  }

  // Create a tank bullet mesh
  createTankBullet(): THREE.Group {
    if (this.projectilePool.length > 0) {
      const bullet = this.projectilePool.pop() as THREE.Group;
      bullet.visible = true;
      return bullet;
    }
    // Build a new mesh if pool empty
    const bulletMaterial = new THREE.MeshStandardMaterial({
      color: 0x303030,
      metalness: 0.9,
      roughness: 0.5,
    });
    const bulletBodyGeometry = new THREE.CylinderGeometry(0.3, 0.3, 2, 32);
    const bulletBody = new THREE.Mesh(bulletBodyGeometry, bulletMaterial);
    const bulletTipGeometry = new THREE.ConeGeometry(0.3, 0.7, 32);
    const bulletTip = new THREE.Mesh(bulletTipGeometry, bulletMaterial);
    bulletTip.position.y = 1.35;
    const bullet = new THREE.Group();
    bullet.add(bulletBody);
    bullet.add(bulletTip);
    bullet.rotation.x = Math.PI / 2;
    bullet.rotation.y = Math.PI / 2;
    bullet.rotation.z = Math.PI / 2;
    return bullet;
  }

  // Weapon management
  private _activeWeapon: string;

  setWeapon(weapon: string) {
    this._activeWeapon = weapon;
  }

  getWeapon(): string {
    return this._activeWeapon;
  }

  private _explosionRadiusForWeapon(weapon: string): number {
    switch (weapon) {
      case 'rocket':
        return 5;
      case 'plasma':
        return 6;
      case 'magic_big':
        return 8;
      case 'magic_1':
        return 4;
      case 'shield':
        return 2;
      default:
        return 3;
    }
  }

  private _getSpeedForWeapon(weapon: string): number {
    switch (weapon) {
      case 'rocket':
        return 140;
      case 'plasma':
        return 110;
      case 'magic_big':
        return 70;
      case 'shield':
        return 60;
      case 'magic_1':
        return 100;
      default:
        return DEFAULT_PROJECTILE_SPEED;
    }
  }

  private _createProjectileForWeapon(weapon: string): THREE.Group {
    switch (weapon) {
      case 'rocket': {
        const mat = new THREE.MeshStandardMaterial({ color: 0xff5533, metalness: 0.3, roughness: 0.6 });
        const body = new THREE.CylinderGeometry(0.35, 0.35, 2.2, 24);
        const tip = new THREE.ConeGeometry(0.35, 0.8, 24);
        const bodyMesh = new THREE.Mesh(body, mat);
        const tipMesh = new THREE.Mesh(tip, mat);
        tipMesh.position.y = 1.5;
        const g = new THREE.Group();
        g.add(bodyMesh);
        g.add(tipMesh);
        g.rotation.x = Math.PI / 2;
        g.rotation.y = Math.PI / 2;
        g.rotation.z = Math.PI / 2;
        return g;
      }
      case 'plasma': {
        const coreMat = new THREE.MeshStandardMaterial({
          color: 0x33ccff,
          emissive: 0x1a88cc,
          emissiveIntensity: 1.2,
          metalness: 0.1,
          roughness: 0.3,
        });
        const core = new THREE.SphereGeometry(0.5, 24, 24);
        const coreMesh = new THREE.Mesh(core, coreMat);

        const auraMat = new THREE.MeshBasicMaterial({ color: 0x66e0ff, transparent: true, opacity: 0.5, blending: THREE.AdditiveBlending });
        const auraGeo = new THREE.TorusGeometry(0.7, 0.12, 16, 32);
        const auraMesh = new THREE.Mesh(auraGeo, auraMat);
        auraMesh.rotation.x = Math.PI / 2;

        const g = new THREE.Group();
        g.add(coreMesh);
        g.add(auraMesh);
        return g;
      }
      case 'magic_big': {
        const coreMat = new THREE.MeshStandardMaterial({
          color: 0xaa66ff,
          emissive: 0x7733cc,
          emissiveIntensity: 1.4,
          metalness: 0.0,
          roughness: 0.2,
        });
        const core = new THREE.IcosahedronGeometry(0.7, 1);
        const coreMesh = new THREE.Mesh(core, coreMat);

        // Dual aura rings for magical feel
        const auraMat1 = new THREE.MeshBasicMaterial({ color: 0xbb88ff, transparent: true, opacity: 0.45, blending: THREE.AdditiveBlending });
        const auraGeo1 = new THREE.TorusGeometry(0.9, 0.12, 16, 48);
        const auraMesh1 = new THREE.Mesh(auraGeo1, auraMat1);
        auraMesh1.rotation.x = Math.PI / 2;

        const auraMat2 = new THREE.MeshBasicMaterial({ color: 0x55ffee, transparent: true, opacity: 0.35, blending: THREE.AdditiveBlending });
        const auraGeo2 = new THREE.TorusGeometry(0.6, 0.1, 16, 48);
        const auraMesh2 = new THREE.Mesh(auraGeo2, auraMat2);
        auraMesh2.rotation.y = Math.PI / 2;

        const g = new THREE.Group();
        g.add(coreMesh);
        g.add(auraMesh1);
        g.add(auraMesh2);
        return g;
      }
      case 'shield': {
        const mat = new THREE.MeshStandardMaterial({ color: 0x66ccff, metalness: 0.1, roughness: 0.2, transparent: true, opacity: 0.8 });
        const ring = new THREE.TorusGeometry(0.6, 0.15, 16, 48);
        const ringMesh = new THREE.Mesh(ring, mat);
        const g = new THREE.Group();
        g.add(ringMesh);
        g.rotation.x = Math.PI / 2;
        return g;
      }
      case 'magic_1': {
        const mat = new THREE.MeshStandardMaterial({ color: 0x55ff88, emissive: 0x116633, emissiveIntensity: 0.6, metalness: 0.2, roughness: 0.3 });
        const sphere = new THREE.SphereGeometry(0.5, 16, 16);
        const sphereMesh = new THREE.Mesh(sphere, mat);
        const g = new THREE.Group();
        g.add(sphereMesh);
        return g;
      }
      default:
        return this.createTankBullet();
    }
  }

  // Precreate projectile meshes and keep them in the pool
  initProjectilePool(count = 10) {
    for (let i = 0; i < count; i++) {
      const mesh = this.createTankBullet();
      mesh.visible = false;
      this.projectilePool.push(mesh);
    }
  }

  // Warm up by touching a projectile mesh once (uploads resources)
  warmup() {
    const mesh = this.createTankBullet();
    this.scene.add(mesh);
    this.scene.remove(mesh);
    mesh.visible = false;
    this.projectilePool.push(mesh);
  }
}

export default ProjectileSystem;

/* eslint-disable unicode-bom */


